import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Shield, User as UserIcon, Trash2, Loader2, Mail, KeyRound } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", badge: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Shield },
  staff: { label: "Staff", badge: "bg-slate-100 text-slate-600 border-slate-200", icon: UserIcon },
};

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", password: "" });
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/members");
      setMembers(data);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addMember = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/members", form);
      toast.success(`${form.name} added as ${form.role}`);
      setForm({ email: "", name: "", role: "staff", password: "" });
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add member");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResetting(true);
    try {
      await api.post(`/members/${resetFor.id}/reset-password`, { password: newPassword });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/members/${m.id}`, { role });
      toast.success(`${m.name} is now ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their access will be revoked immediately.`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400 mb-1">Access Control</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Team &amp; Staff</h1>
          <p className="text-sm text-slate-500 mt-1">Add society admins and staff. Each member signs in with their email and password.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-[#0F2942] gap-2" data-testid="add-member-button">
          <UserPlus className="h-4 w-4" /> Add Member
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading team…</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="members-list">
          {members.map((m, i) => {
            const meta = ROLE_META[m.role] || ROLE_META.staff;
            const isSelf = m.email === user?.email;
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-0"
                data-testid={`member-row-${m.id}`}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-[#0F2942] text-white text-xs">{m.name?.slice(0, 2)?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 truncate">{m.name}</p>
                    {isSelf && <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">You</span>}
                    {m.status !== "active" && <span className="text-[10px] font-mono uppercase text-amber-600">Inactive</span>}
                  </div>
                  <p className="text-xs text-slate-400 font-mono flex items-center gap-1"><Mail className="h-3 w-3" /> {m.email}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                  <meta.icon className="h-3 w-3" /> {meta.label}
                </span>
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)} disabled={isSelf}>
                  <SelectTrigger className="w-28 h-8" data-testid={`member-role-select-${m.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="icon" variant="ghost"
                  className="h-8 w-8 text-slate-500"
                  title="Reset password"
                  onClick={() => { setResetFor(m); setNewPassword(""); }}
                  data-testid={`member-reset-${m.id}`}
                >
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button
                  size="icon" variant="ghost"
                  className="h-8 w-8 text-red-500 disabled:opacity-30"
                  disabled={isSelf}
                  onClick={() => removeMember(m)}
                  data-testid={`member-delete-${m.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </motion.div>
            );
          })}
          {members.length === 0 && <p className="text-sm text-slate-400 p-8 text-center">No members yet.</p>}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" data-testid="member-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Login Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@society.com" data-testid="member-email-input" />
              <p className="text-[11px] text-slate-400">They sign in with this email and password.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Initial Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="At least 6 characters" data-testid="member-password-input" />
              <p className="text-[11px] text-slate-400">Share this with the user; they can reset it later.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="member-role-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access incl. team &amp; deletions</SelectItem>
                  <SelectItem value="staff">Staff — add flats &amp; tenancies only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="member-cancel-button">Cancel</Button>
            <Button onClick={addMember} disabled={saving} className="bg-[#0F2942] gap-2" data-testid="member-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent className="max-w-md" data-testid="reset-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Set a new password for <span className="font-medium text-slate-800">{resetFor?.name}</span> ({resetFor?.email}). Their existing sessions will be signed out.</p>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">New Password</Label>
              <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" data-testid="reset-member-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)} data-testid="reset-member-cancel-button">Cancel</Button>
            <Button onClick={resetPassword} disabled={resetting} className="bg-[#0F2942] gap-2" data-testid="reset-member-save-button">
              {resetting && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
